// EditMyListing.js
// ✅ Full desktop-structured Edit Listing screen (with DesktopSideNav)
// ✅ SAME endpoints + SAME update logic + SAME dropdown/time modal logic
// ✅ ListingID from route params OR web query (?ListingID=123)
// ✅ "Edit images" opens ListingImageUpload.html in SAME browser tab (web)
// ✅ Offer preview QR ONLY (no “open preview” link/button)
// ✅ QR card is compact + snug (not wide)
// ✅ Back button fallback for web direct-link cases
// ✅ No “current saved” text anywhere

import React, { useEffect, useMemo, useState } from "react";
import {
  StyleSheet,
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Alert,
  ScrollView,
  SafeAreaView,
  Linking,
  Platform,
  useWindowDimensions,
  Image,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import QRCode from "react-native-qrcode-svg";

/* -------------------- OPTIONS (unchanged) -------------------- */
const followerOptions = [
  { label: "No min.", value: 0 },
  { label: "2k", value: 2000 },
  { label: "5k", value: 5000 },
  { label: "10k", value: 10000 },
  { label: "15k", value: 15000 },
  { label: "20k", value: 20000 },
  { label: "25k", value: 25000 },
  { label: "30k", value: 30000 },
  { label: "35k", value: 35000 },
  { label: "40k", value: 40000 },
  { label: "45k", value: 45000 },
  { label: "50k", value: 50000 },
  { label: "60k", value: 60000 },
  { label: "70k", value: 70000 },
  { label: "80k", value: 80000 },
  { label: "90k", value: 90000 },
  { label: "100k", value: 100000 },
  { label: "150k", value: 150000 },
  { label: "200k", value: 200000 },
  { label: "250k", value: 250000 },
  { label: "300k", value: 300000 },
  { label: "350k", value: 350000 },
  { label: "400k", value: 400000 },
  { label: "450k", value: 450000 },
  { label: "500k", value: 500000 },
];

const timeOptions = [
  "-", // Blank option
  "6:00 AM",
  "6:30 AM",
  "7:00 AM",
  "7:30 AM",
  "8:00 AM",
  "8:30 AM",
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
  "5:30 PM",
  "6:00 PM",
  "6:30 PM",
  "7:00 PM",
  "7:30 PM",
  "8:00 PM",
  "8:30 PM",
  "9:00 PM",
  "9:30 PM",
  "10:00 PM",
  "10:30 PM",
  "11:00 PM",
  "11:30 PM",
  "12:00 AM",
  "12:30 AM",
  "1:00 AM",
  "1:30 AM",
];

/* -------------------- WEB PARAM HELPERS -------------------- */
const readWebQueryParams = () => {
  try {
    if (typeof window === "undefined") return {};
    const search = window.location.search || "";
    const sp = new URLSearchParams(search);
    const out = {};
    for (const [k, v] of sp.entries()) out[k] = v;
    return out;
  } catch (e) {
    return {};
  }
};

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

/* ---------------- SIDE NAV (same as your desktop style) ---------------- */
const DesktopSideNav = ({ navigation, activeKey = "" }) => {
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState("none"); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === "subscriber";

  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/GetSessionData.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => setUserData(d || {}))
      .catch((e) => console.error("SideNav session fetch error:", e));
  }, []);

  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php", {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data?.authenticated === "boolean"
            ? data.authenticated
            : data?.status === "active";
        const mode = data?.auth_mode
          ? data.auth_mode
          : data?.status === "active"
          ? "subscriber"
          : "none";

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error("SideNav auth fetch error:", err))
      .finally(() => setAuthChecked(true));
  }, []);

  const openInSameTab = (url) => {
    if (typeof window !== "undefined") window.location.href = url;
    else Linking.openURL(url);
  };

  const NavItem = ({ itemKey, label, icon, onPress, locked }) => {
    const isActive = activeKey && activeKey === itemKey;
    return (
      <TouchableOpacity
        style={[snav.navItem, isActive && snav.navItemActive]}
        onPress={onPress}
        activeOpacity={0.85}
      >
        <View style={snav.navItemRow}>
          <View style={snav.navItemMainLabel}>
            <Ionicons name={icon} size={20} color="#f5f5f5" style={snav.navIcon} />
            <Text style={snav.navText}>{label}</Text>
          </View>

          {locked ? (
            <Ionicons
              name="lock-closed-outline"
              size={16}
              color="#FDF05D"
              style={snav.lockIcon}
            />
          ) : null}
        </View>
      </TouchableOpacity>
    );
  };

  const lockedCalendar = authChecked && !isAuthed;
  const lockedActivity = authChecked && !isAuthed;

  return (
    <View style={snav.sideNav}>
      <LinearGradient colors={["#050509", "#050505", "#000000"]} style={snav.sideNavInner}>
        <LinearGradient
          colors={["rgba(253,240,93,0.08)", "rgba(0,0,0,0)"]}
          style={snav.sideNavGlow}
          start={{ x: 0.1, y: 0 }}
          end={{ x: 1, y: 1 }}
        />

        <View style={snav.sideNavHeader}>
          <Image
            source={{ uri: "https://collabbayadmin.com/CollabbayLogoDark.png" }}
            style={snav.sideNavLogoImage}
            resizeMode="contain"
          />
          <View>
            <Text style={snav.logoWordmark}>
              <Text style={{ fontWeight: "300" }}>collab</Text>
              bay
              <Text style={snav.logoDot}>.</Text>
            </Text>
            <Text style={snav.logoDesktopTag}>DESKTOP</Text>
          </View>
        </View>

        <View style={snav.sideNavSection}>
          <LinearGradient
            colors={["#111111", "#2b2b2b"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={snav.usernamePill}
          >
            <Text numberOfLines={1} ellipsizeMode="tail" style={snav.usernamePillText}>
              {userData?.Username ? ` ${userData.Username}` : "Welcome Back"}
            </Text>
          </LinearGradient>

          <NavItem itemKey="MyPanel" label="Home" icon="home-outline" onPress={() => navigation.navigate("MyPanel")} />
          <NavItem itemKey="MyOffers" label="My Offers" icon="pricetags-outline" onPress={() => navigation.navigate("MyOffers")} />
          <NavItem
            itemKey="MyCalendar"
            label="My Calendar"
            icon="calendar-outline"
            locked={lockedCalendar}
            onPress={() => {
              if (lockedCalendar) openInSameTab("https://collabbayadmin.com/subscribe");
              else navigation.navigate("MyCalendar");
            }}
          />
          <NavItem itemKey="BrowseCreators" label="Browse Creators" icon="people-outline" onPress={() => navigation.navigate("BrowseCreators")} />
          <NavItem
            itemKey="MyActivityFeed"
            label="Activity"
            icon="chatbubbles-outline"
            locked={lockedActivity}
            onPress={() => {
              if (lockedActivity) openInSameTab("https://collabbayadmin.com/subscribe");
              else navigation.navigate("MyActivityFeed");
            }}
          />
          <NavItem itemKey="BusinessAccountPage" label="Account" icon="person-circle-outline" onPress={() => navigation.navigate("BusinessAccountPage")} />
        </View>

        <View style={snav.sideNavSection}>
          <TouchableOpacity
            style={snav.peerMapCard}
            activeOpacity={0.9}
            onPress={() => openInSameTab("https://collabbayadmin.com/venues")}
          >
            <LinearGradient
              colors={["rgba(255,255,255,0.08)", "rgba(0,0,0,0.4)"]}
              style={snav.peerMapInner}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <View style={snav.peerMapContent}>
                <Ionicons name="map-outline" size={18} color="#FDF05D" style={{ marginRight: 8 }} />
                <View style={{ flex: 1 }}>
                  <Text style={snav.peerMapTitle}>Peer Map</Text>
                  <Text style={snav.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                </View>
              </View>
            </LinearGradient>
          </TouchableOpacity>

          {!isSubscriber ? (
            <TouchableOpacity
              style={snav.subscribeCard}
              activeOpacity={0.9}
              onPress={() => openInSameTab("https://collabbayadmin.com/subscribe")}
            >
              <LinearGradient
                colors={["#FDF05D", "#fbbf24"]}
                style={snav.subscribeGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                <View style={snav.subscribeContent}>
                  <Ionicons name="rocket-outline" size={20} color="#000" style={{ marginRight: 8 }} />
                  <View style={{ flex: 1 }}>
                    <Text style={snav.subscribeTitle}>Subscribe & unlock</Text>
                    <Text style={snav.subscribeSubtitle}>Unlock Activity & Calendar, plus more.</Text>
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={snav.sideNavBottom}>
          <TouchableOpacity style={snav.bottomLink} onPress={() => openInSameTab("https://collabbayadmin.com/FAQs")}>
            <Ionicons name="help-circle-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
            <View>
              <Text style={snav.bottomLinkText}>FAQs</Text>
              <Text style={snav.bottomLinkCaption}>Common questions answered</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity style={snav.bottomLink} onPress={() => Linking.openURL("mailto:info@collab-bay.com")}>
            <Ionicons name="headset-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
            <View>
              <Text style={snav.bottomLinkText}>Help</Text>
              <Text style={snav.bottomLinkCaption}>We&apos;re here 24/7</Text>
            </View>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
};

/* ---------------- MAIN SCREEN ---------------- */
export default function EditMyListing({ route, navigation }) {
  const { width } = useWindowDimensions();
  const isDesktop = Platform.OS === "web" && width >= 980;
  const isWide = Platform.OS === "web" && width >= 1100;

  // ✅ ListingID can come from route params OR ?ListingID= in URL
  const webQuery = useMemo(() => (Platform.OS === "web" ? readWebQueryParams() : {}), []);
  const ListingID = pickFirstNonEmpty(
    route?.params?.ListingID,
    route?.params?.listingid,
    route?.params?.listingID,
    webQuery?.ListingID,
    webQuery?.listingid,
    webQuery?.listingID
  );

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [isTimeModalVisible, setIsTimeModalVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedTimeType, setSelectedTimeType] = useState("");
  const [minFollowersRequired, setMinFollowersRequired] = useState("No min.");
  const [advanceDays, setAdvanceDays] = useState(""); // '' = no restriction

  const openInSameTab = (url) => {
    if (typeof window !== "undefined") {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const handleBack = () => {
    try {
      const canGo =
        typeof navigation?.canGoBack === "function" ? navigation.canGoBack() : false;

      if (canGo) return navigation.goBack();

      if (Platform.OS === "web" && typeof window !== "undefined" && window.history?.length > 1) {
        window.history.back();
        return;
      }

      navigation.navigate("MyOffers");
    } catch (e) {
      navigation.navigate("MyOffers");
    }
  };

  const imageUploadUrl = ListingID
    ? `https://collabbayadmin.com/ListingImageUpload.html?ListingID=${encodeURIComponent(ListingID)}`
    : null;

  const previewUrl = ListingID ? `https://collabbay.com/collaboffer/${ListingID}` : null;

  const defaultAvailability = {
    Monday: { open: "", close: "" },
    Tuesday: { open: "", close: "" },
    Wednesday: { open: "", close: "" },
    Thursday: { open: "", close: "" },
    Friday: { open: "", close: "" },
    Saturday: { open: "", close: "" },
    Sunday: { open: "", close: "" },
  };

  const mergedAvailability = {
    ...defaultAvailability,
    ...formData.PreferredAvailability,
  };

  const parseAvailability = (availabilityString) => {
    const availabilityObj = {};
    const dayEntries = String(availabilityString || "")
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

    dayEntries.forEach((dayEntry) => {
      const [day, timeRange] = dayEntry.split(": ");
      if (timeRange) {
        const [openTime, closeTime] = timeRange.split(" - ");
        availabilityObj[day] = { open: openTime, close: closeTime };
      }
    });

    return availabilityObj;
  };

  useEffect(() => {
    const fetchListingData = async () => {
      try {
        const response = await fetch(
          `https://collabbayadmin.com/APIControllers/GetListingByID.php?ListingID=${encodeURIComponent(
            ListingID || ""
          )}`
        );
        const result = await response.json();

        if (response.ok) {
          const contentBriefs = (result.ContentBrief || "")
            .split(",")
            .map((item) => item.trim());

          const availability = parseAvailability(result.PreferredAvailability || "");

          setFormData({
            ...result,
            ContentBrief1: contentBriefs[0] || "",
            ContentBrief2: contentBriefs[1] || "",
            ContentBrief3: contentBriefs[2] || "",
            PreferredAvailability: availability,
          });

          setMinFollowersRequired(result.MinFollowersRequired || "No min.");
          setAdvanceDays(
            result.AdvanceDays === null || result.AdvanceDays === undefined
              ? ""
              : String(result.AdvanceDays)
          );
        } else {
          Alert.alert("Error", result.error || "Failed to fetch listing data.");
        }
      } catch (error) {
        Alert.alert("Error", "An error occurred while fetching the listing data.");
      } finally {
        setLoading(false);
      }
    };

    if (!ListingID) {
      setLoading(false);
      return;
    }

    fetchListingData();
  }, [ListingID]);

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleDropdown = () => setDropdownVisible(!isDropdownVisible);

  const selectMinFollowersOption = (option) => {
    setMinFollowersRequired(option.label);
    setDropdownVisible(false);
  };

  const handleTimeSelection = (day, type, time) => {
    const selectedTime = time === "-" ? "" : time;
    setFormData((prev) => {
      const updatedAvailability = { ...(prev.PreferredAvailability || {}) };
      if (!updatedAvailability[day]) updatedAvailability[day] = { open: "", close: "" };
      updatedAvailability[day][type] = selectedTime;
      return { ...prev, PreferredAvailability: updatedAvailability };
    });
    setIsTimeModalVisible(false);
  };

  const openTimeModal = (day, type) => {
    setSelectedDay(day);
    setSelectedTimeType(type);
    setIsTimeModalVisible(true);
  };

  const handleSubmit = async () => {
    if (!termsAccepted) {
      Alert.alert("Terms of Use", "You must agree to the terms of use before submitting.");
      return;
    }

    if (!formData.Location) return Alert.alert("Error", "Location is required.");
    if (!formData.ListingDescription) return Alert.alert("Error", "Listing description is required.");
    if (!formData.AdHeader) return Alert.alert("Error", "Ad header is required.");
    if (!formData.BrandURL) return Alert.alert("Error", "Brand URL is required.");

    const contentBrief = [formData.ContentBrief1 || "", formData.ContentBrief2 || "", formData.ContentBrief3 || ""]
      .filter((brief) => brief.trim() !== "")
      .join(", ");

    if (!contentBrief) {
      Alert.alert("Error", "At least one content brief is required.");
      return;
    }

    const formattedAvailability = Object.entries(mergedAvailability)
      .filter(([day, times]) => times.open && times.close)
      .map(([day, times]) => `${day}: ${times.open} - ${times.close}`)
      .join(", ");

    if (!formattedAvailability) {
      Alert.alert("Error", "Please provide availability with at least one open and close time.");
      return;
    }

    if (advanceDays !== "") {
      const advNum = parseInt(advanceDays, 10);
      if (Number.isNaN(advNum) || advNum < 0 || advNum > 60) {
        Alert.alert("Error", "Advance days must be a whole number between 0 and 60 (or leave blank).");
        return;
      }
    }

    const payload = {
      ...formData,
      ListingID,
      MinFollowersRequired: minFollowersRequired || "No min.",
      PreferredAvailability: formattedAvailability,
      ContentBrief: contentBrief,
      NonCreditOffer: formData.NonCreditOffer || "",
      TurnAroundDays: formData.TurnAroundDays || null,
      AdvanceDays: advanceDays === "" ? null : parseInt(advanceDays, 10),
    };

    try {
      const response = await fetch("https://collabbayadmin.com/APIControllers/UpdateListing.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.message === "Data updated successfully") {
        Alert.alert("Success", "Listing updated successfully.");
        handleBack();
      } else {
        Alert.alert("Error", result.error || "Failed to update listing.");
      }
    } catch (error) {
      console.error("API Error:", error);
      Alert.alert("Error", "An error occurred while updating the listing.");
    }
  };

  /* -------------------- UI HELPERS -------------------- */
  const Field = ({ icon, label, children, hint, required = false, valueForCheck }) => {
    const hasValue =
      valueForCheck === undefined || valueForCheck === null
        ? true
        : String(valueForCheck).trim() !== "";

    const showEmptyRequired = required && !hasValue;

    return (
      <View style={ui.fieldWrap}>
        <View style={ui.fieldLabelRow}>
          <Text style={ui.fieldLabel}>{label}</Text>
          <View style={[ui.pill, required ? ui.pillRequired : ui.pillOptional]}>
            <Text style={[ui.pillText, required ? ui.pillTextRequired : ui.pillTextOptional]}>
              {required ? "Required" : "Optional"}
            </Text>
          </View>
          {showEmptyRequired ? (
            <View style={[ui.pill, ui.pillEmpty]}>
              <Text style={[ui.pillText, ui.pillTextEmpty]}>Empty</Text>
            </View>
          ) : (
            <View style={[ui.pill, ui.pillFilled]}>
              <Text style={[ui.pillText, ui.pillTextFilled]}>Filled</Text>
            </View>
          )}
        </View>

        {hint ? <Text style={ui.fieldHint}>{hint}</Text> : null}

        <View style={[ui.fieldRow, showEmptyRequired && ui.fieldRowEmptyRequired]}>
          <View style={ui.fieldIcon}>
            <Ionicons name={icon} size={18} color="#111" />
          </View>
          <View style={{ flex: 1 }}>{children}</View>
        </View>
      </View>
    );
  };

  const Card = ({ title, subtitle, children, right }) => (
    <View style={ui.card}>
      <View style={ui.cardHeader}>
        <View style={{ flex: 1 }}>
          <Text style={ui.cardTitle}>{title}</Text>
          {subtitle ? <Text style={ui.cardSubtitle}>{subtitle}</Text> : null}
        </View>
        {right ? <View style={{ marginLeft: 12 }}>{right}</View> : null}
      </View>
      <View style={ui.cardBody}>{children}</View>
    </View>
  );

  /* -------------------- LOADING -------------------- */
  if (loading) {
    return (
      <View style={shell.root}>
        <SafeAreaView style={shell.safeAreaShell}>
          <View style={shell.shellRow}>
            {isDesktop ? <DesktopSideNav navigation={navigation} activeKey="MyOffers" /> : null}
            <View style={shell.mainArea}>
              <View style={ui.loadingWrap}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={ui.loadingText}>Loading listing…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  /* -------------------- NO LISTING ID -------------------- */
  if (!ListingID) {
    return (
      <View style={shell.root}>
        <SafeAreaView style={shell.safeAreaShell}>
          <View style={shell.shellRow}>
            {isDesktop ? <DesktopSideNav navigation={navigation} activeKey="MyOffers" /> : null}
            <View style={shell.mainArea}>
              <View style={ui.loadingWrap}>
                <Text style={ui.errorText}>Missing ListingID.</Text>
                <TouchableOpacity style={ui.primaryBtn} onPress={handleBack} activeOpacity={0.9}>
                  <Ionicons name="arrow-back" size={18} color="#000" style={{ marginRight: 8 }} />
                  <Text style={ui.primaryBtnText}>Go back</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  /* -------------------- MAIN -------------------- */
  return (
    <View style={shell.root}>
      <SafeAreaView style={shell.safeAreaShell}>
        <View style={shell.shellRow}>
          {isDesktop ? <DesktopSideNav navigation={navigation} activeKey="MyOffers" /> : null}

          <View style={shell.mainArea}>
            <ScrollView contentContainerStyle={ui.pageScroll} showsVerticalScrollIndicator={false}>
              <View style={ui.pageInner}>
                {/* Top Bar */}
                <View style={ui.topBar}>
                  <TouchableOpacity onPress={handleBack} style={ui.backBtn} activeOpacity={0.85}>
                    <Ionicons name="arrow-back" size={18} color="#000" />
                    <Text style={ui.backBtnText}>Back</Text>
                  </TouchableOpacity>

                  <View style={{ flex: 1, minWidth: 220 }}>
                    <Text style={ui.pageTitle}>Edit listing</Text>
                    <Text style={ui.pageMeta}>ListingID: #{ListingID}</Text>
                  </View>

                  <View style={ui.topActions}>
                    {/* ✅ More prominent "Edit images" */}
                    <TouchableOpacity
                      style={ui.editImagesBtn}
                      activeOpacity={0.9}
                      onPress={() => {
                        if (!imageUploadUrl) return;
                        openInSameTab(imageUploadUrl);
                      }}
                    >
                      <Ionicons name="images-outline" size={18} color="#000" style={{ marginRight: 10 }} />
                      <View style={{ alignItems: "flex-start" }}>
                        <Text style={ui.editImagesBtnTitle}>Edit images</Text>
                        <Text style={ui.editImagesBtnSub}>Upload / replace listing photo</Text>
                      </View>
                      <Ionicons name="chevron-forward" size={18} color="#000" style={{ marginLeft: 12, opacity: 0.7 }} />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={ui.newOfferBtn}
                      activeOpacity={0.9}
                      onPress={() => navigation.navigate("GuidedOfferSetup")}
                    >
                      <Text style={ui.newOfferBtnText}>Create new offer</Text>
                      <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Note + compact QR */}
                <View style={ui.topInfoRow}>
                  <View style={ui.noteBox}>
                    <Ionicons name="information-circle-outline" size={18} color="#000" style={{ marginRight: 10 }} />
                    <Text style={ui.noteText}>
                      <Text style={{ fontWeight: "700" }}>Note:</Text> this offer has already been posted — details below
                      are editable, but you can’t change the underlying offer identity.
                    </Text>
                  </View>

                  {/* ✅ QR ONLY, compact, snug */}
                  <View style={ui.qrMiniCard}>
                    <View style={ui.qrMiniHeader}>
                      <View style={ui.qrBadge}>
                        <Ionicons name="qr-code-outline" size={14} color="#111827" />
                      </View>
                      <Text style={ui.qrMiniTitle}>Preview Offer</Text>
                    </View>

                    <Text style={ui.qrMiniSubtitle}>Scan to preview as a creator</Text>

                    <View style={ui.qrMiniCodeWrap}>
                      <QRCode value={previewUrl} size={86} />
                    </View>

                    <Text style={ui.qrMiniUrlText} numberOfLines={1} ellipsizeMode="middle">
                      collabbay.com/collaboffer/{ListingID}
                    </Text>
                  </View>
                </View>

                {/* Desktop grid */}
                <View style={[ui.gridRow, !isWide && { flexDirection: "column" }]}>
                  {/* LEFT COL */}
                  <View style={[ui.col, !isWide && { width: "100%" }]}>
                    <Card title="General information" subtitle="Basics shown to creators">
                      <Field icon="location-outline" label="Location" required valueForCheck={formData.Location}>
                        <TextInput
                          style={ui.input}
                          placeholder="Enter location"
                          placeholderTextColor="#777"
                          value={formData.Location || ""}
                          onChangeText={(value) => handleInputChange("Location", value)}
                        />
                      </Field>

                      <Field
                        icon="text-outline"
                        label="Listing description"
                        required
                        valueForCheck={formData.ListingDescription}
                        hint="Explain what creators get and what you expect. Keep it clear + specific."
                      >
                        <TextInput
                          style={[ui.input, ui.textarea]}
                          placeholder="Write the listing description"
                          placeholderTextColor="#777"
                          multiline
                          value={formData.ListingDescription || ""}
                          onChangeText={(value) => handleInputChange("ListingDescription", value)}
                        />
                      </Field>
                    </Card>

                    <Card title="Offer details" subtitle="What creators see + your CTA link">
                      <Field icon="newspaper-outline" label="Ad header" required valueForCheck={formData.AdHeader}>
                        <TextInput
                          style={ui.input}
                          placeholder="e.g., Free tasting menu for creators"
                          placeholderTextColor="#777"
                          value={formData.AdHeader || ""}
                          onChangeText={(value) => handleInputChange("AdHeader", value)}
                        />
                      </Field>

                      <Field icon="link-outline" label="Brand URL" required valueForCheck={formData.BrandURL} hint="Used as your CTA link.">
                        <TextInput
                          style={ui.input}
                          placeholder="https://example.com"
                          placeholderTextColor="#777"
                          value={formData.BrandURL || ""}
                          onChangeText={(value) => handleInputChange("BrandURL", value)}
                          autoCapitalize="none"
                        />
                      </Field>

                      <Field icon="pricetag-outline" label="Value (£20–£1000)" required valueForCheck={formData.Credits?.toString()} hint="What is the menu-value of this offer.">
                        <TextInput
                          style={ui.input}
                          placeholder="e.g., 250"
                          placeholderTextColor="#777"
                          keyboardType="numeric"
                          value={formData.Credits?.toString() || ""}
                          onChangeText={(value) => handleInputChange("Credits", value.replace(/[^0-9]/g, ""))}
                        />
                      </Field>

                      <Field
                        icon="calendar-outline"
                        label="Minimum days in advance"
                        required={false}
                        valueForCheck={advanceDays}
                        hint="Creators must book at least this many days ahead. Leave blank to allow any-time booking."
                      >
                        <TextInput
                          style={ui.input}
                          placeholder="e.g., 4"
                          placeholderTextColor="#777"
                          keyboardType="numeric"
                          value={advanceDays}
                          onChangeText={(t) => setAdvanceDays(t.replace(/[^0-9]/g, "").slice(0, 2))}
                        />
                      </Field>

                      <Field
                        icon="people-outline"
                        label="Minimum followers"
                        required={false}
                        valueForCheck={minFollowersRequired}
                        hint="Helps filter who can request this offer."
                      >
                        <TouchableOpacity style={ui.dropdownBtn} onPress={toggleDropdown} activeOpacity={0.9}>
                          <Text style={ui.dropdownBtnText}>{minFollowersRequired}</Text>
                          <Ionicons
                            name={isDropdownVisible ? "chevron-up-outline" : "chevron-down-outline"}
                            size={18}
                            color="#000"
                          />
                        </TouchableOpacity>

                        {isDropdownVisible ? (
                          <View style={ui.dropdownPanel}>
                            <ScrollView style={{ maxHeight: 240 }} showsVerticalScrollIndicator={false}>
                              {followerOptions.map((option) => (
                                <TouchableOpacity
                                  key={option.value}
                                  style={ui.dropdownItem}
                                  onPress={() => selectMinFollowersOption(option)}
                                  activeOpacity={0.85}
                                >
                                  <Text style={ui.dropdownItemText}>{option.label}</Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          </View>
                        ) : null}
                      </Field>
                    </Card>
                  </View>

                  {/* RIGHT COL */}
                  <View style={[ui.col, !isWide && { width: "100%" }]}>
                    <Card title="Content brief" subtitle="Up to 3 bullet points creators must follow">
                      <Field icon="bulb-outline" label="Brief point 1" required valueForCheck={formData.ContentBrief1} hint="At least 1 brief point is required.">
                        <TextInput
                          style={ui.input}
                          placeholder="e.g., Mention our new menu"
                          placeholderTextColor="#777"
                          value={formData.ContentBrief1 || ""}
                          onChangeText={(value) => handleInputChange("ContentBrief1", value)}
                        />
                      </Field>

                      <Field icon="bulb-outline" label="Brief point 2" required={false} valueForCheck={formData.ContentBrief2}>
                        <TextInput
                          style={ui.input}
                          placeholder="Optional"
                          placeholderTextColor="#777"
                          value={formData.ContentBrief2 || ""}
                          onChangeText={(value) => handleInputChange("ContentBrief2", value)}
                        />
                      </Field>

                      <Field icon="bulb-outline" label="Brief point 3" required={false} valueForCheck={formData.ContentBrief3}>
                        <TextInput
                          style={ui.input}
                          placeholder="Optional"
                          placeholderTextColor="#777"
                          value={formData.ContentBrief3 || ""}
                          onChangeText={(value) => handleInputChange("ContentBrief3", value)}
                        />
                      </Field>
                    </Card>

                    <Card title="Availability" subtitle="Leave a timeslot blank if closed">
                      <View style={ui.availabilityWrap}>
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                          <View key={day} style={ui.avRow}>
                            <Text style={ui.avDay}>{day}</Text>

                            <TouchableOpacity
                              style={[
                                ui.timeBtn,
                                !(formData.PreferredAvailability?.[day]?.open || "") && ui.timeBtnEmpty,
                              ]}
                              onPress={() => openTimeModal(day, "open")}
                              activeOpacity={0.9}
                            >
                              <Text style={ui.timeBtnText}>
                                {formData.PreferredAvailability?.[day]?.open || "-"}
                              </Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                              style={[
                                ui.timeBtn,
                                !(formData.PreferredAvailability?.[day]?.close || "") && ui.timeBtnEmpty,
                              ]}
                              onPress={() => openTimeModal(day, "close")}
                              activeOpacity={0.9}
                            >
                              <Text style={ui.timeBtnText}>
                                {formData.PreferredAvailability?.[day]?.close || "-"}
                              </Text>
                            </TouchableOpacity>
                          </View>
                        ))}
                      </View>
                    </Card>

                    <Card title="Terms" subtitle="Required to save changes">
                      <View style={ui.termsRow}>
                        <TouchableOpacity onPress={() => setTermsAccepted(!termsAccepted)} style={ui.checkbox}>
                          <Ionicons
                            name={termsAccepted ? "checkbox-outline" : "square-outline"}
                            size={22}
                            color="#000"
                          />
                        </TouchableOpacity>

                        <Text style={ui.termsText}>
                          By saving this offer I agree with{" "}
                          <Text
                            style={ui.linkText}
                            onPress={() => Linking.openURL("https://collabbay.com/PrivacyPolicy.html")}
                          >
                            Collabbay terms of use
                          </Text>
                          .
                        </Text>
                      </View>

                      <TouchableOpacity style={ui.primaryBtn} onPress={handleSubmit} activeOpacity={0.9}>
                        <Text style={ui.primaryBtnText}>Save changes</Text>
                        <Ionicons name="checkmark" size={18} color="#000" style={{ marginLeft: 10 }} />
                      </TouchableOpacity>
                    </Card>
                  </View>
                </View>

                {/* Time Modal */}
                <Modal
                  visible={isTimeModalVisible}
                  transparent={true}
                  animationType="slide"
                  onRequestClose={() => setIsTimeModalVisible(false)}
                >
                  <View style={ui.modalOverlay}>
                    <View style={ui.modalCard}>
                      <Text style={ui.modalTitle}>Select time</Text>
                      <Text style={ui.modalMeta}>
                        {selectedDay} • {selectedTimeType === "open" ? "Open" : "Close"}
                      </Text>

                      <ScrollView style={ui.timeList} showsVerticalScrollIndicator={false}>
                        {timeOptions.map((time, index) => (
                          <TouchableOpacity
                            key={index}
                            style={ui.timeItem}
                            onPress={() => handleTimeSelection(selectedDay, selectedTimeType, time)}
                            activeOpacity={0.85}
                          >
                            <Text style={ui.timeText}>{time}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>

                      <TouchableOpacity
                        style={ui.modalCancel}
                        onPress={() => setIsTimeModalVisible(false)}
                        activeOpacity={0.9}
                      >
                        <Text style={ui.modalCancelText}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </Modal>

                <View style={{ height: 26 }} />
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

/* -------------------- SHELL STYLES -------------------- */
const shell = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#f3f4f6" },
  safeAreaShell: { flex: 1 },
  shellRow: { flex: 1, flexDirection: "row" },
  mainArea: { flex: 1, backgroundColor: "#f3f3f3" },
});

/* -------------------- UI STYLES -------------------- */
const ui = StyleSheet.create({
  pageScroll: {
    paddingVertical: 18,
    paddingHorizontal: 18,
    alignItems: "center",
    alignSelf: "center",
  },
  pageInner: {
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
  },

  topBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  backBtn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#eee",
  },
  backBtnText: { marginLeft: 8, fontSize: 13, fontWeight: "600", color: "#111" },

  pageTitle: { fontSize: 24, fontWeight: "700", color: "#111" },
  pageMeta: { marginTop: 2, fontSize: 12, color: "#666" },

  topActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginLeft: "auto",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  editImagesBtn: {
    backgroundColor: "#FDF05D",
    borderRadius: 18,
    paddingVertical: 12,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2,
  },
  editImagesBtnTitle: { fontSize: 13, fontWeight: "900", color: "#000" },
  editImagesBtnSub: { fontSize: 11, color: "#111", opacity: 0.75, marginTop: 2 },

  newOfferBtn: {
    backgroundColor: "#fff",
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  newOfferBtnText: { color: "#000", fontWeight: "800", fontSize: 13 },

  topInfoRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "stretch",
    marginBottom: 14,
    flexWrap: "wrap",
  },

  noteBox: {
    flex: 1,
    minWidth: 340,
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#fff",
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "#eee",
  },
  noteText: { flex: 1, color: "#111", fontSize: 13, lineHeight: 18 },

  // Compact QR card
  qrMiniCard: {
    width: 260,
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    padding: 12,
    alignSelf: "flex-start",
  },
  qrMiniHeader: { flexDirection: "row", alignItems: "center" },
  qrBadge: {
    width: 22,
    height: 22,
    borderRadius: 999,
    backgroundColor: "#fdf05d",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  qrMiniTitle: { fontSize: 13, fontWeight: "800", color: "#111827" },
  qrMiniSubtitle: { marginTop: 6, fontSize: 11, color: "#6b7280" },
  qrMiniCodeWrap: { marginTop: 10, alignItems: "center", justifyContent: "center" },
  qrMiniUrlText: { marginTop: 10, fontSize: 10, color: "#6b7280" },

  gridRow: { flexDirection: "row", gap: 14 },
  col: { width: "50%" },

  card: {
    backgroundColor: "#fff",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#eee",
    padding: 14,
    marginBottom: 14,
  },
  cardHeader: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  cardTitle: { fontSize: 16, fontWeight: "800", color: "#111" },
  cardSubtitle: { marginTop: 4, fontSize: 12, color: "#666", lineHeight: 16 },
  cardBody: { marginTop: 6 },

  fieldWrap: { marginBottom: 12 },
  fieldLabelRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" },
  fieldLabel: { fontSize: 13, fontWeight: "800", color: "#111" },
  fieldHint: { fontSize: 12, color: "#777", marginBottom: 8, lineHeight: 16 },

  pill: { paddingVertical: 3, paddingHorizontal: 8, borderRadius: 999, borderWidth: 1 },
  pillText: { fontSize: 10, fontWeight: "900" },
  pillRequired: { backgroundColor: "#fff7ed", borderColor: "#fed7aa" },
  pillOptional: { backgroundColor: "#f3f4f6", borderColor: "#e5e7eb" },
  pillTextRequired: { color: "#9a3412" },
  pillTextOptional: { color: "#374151" },

  pillFilled: { backgroundColor: "#ecfdf5", borderColor: "#bbf7d0" },
  pillTextFilled: { color: "#166534" },

  pillEmpty: { backgroundColor: "#fff1f2", borderColor: "#fecdd3" },
  pillTextEmpty: { color: "#9f1239" },

  fieldRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#eee",
    overflow: "hidden",
  },
  fieldRowEmptyRequired: { borderColor: "#fecdd3", backgroundColor: "#fff1f2" },
  fieldIcon: {
    width: 42,
    height: 42,
    justifyContent: "center",
    alignItems: "center",
    borderRightWidth: 1,
    borderRightColor: "#e9e9e9",
    backgroundColor: "transparent",
  },

  input: { height: 42, paddingHorizontal: 12, color: "#111", fontSize: 14 },
  textarea: { height: 110, paddingTop: 10, paddingBottom: 10, textAlignVertical: "top" },

  dropdownBtn: {
    height: 42,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropdownBtnText: { color: "#111", fontWeight: "900" },
  dropdownPanel: { marginTop: 8, backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#eee", overflow: "hidden" },
  dropdownItem: { paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: "#f1f1f1" },
  dropdownItemText: { color: "#111", fontSize: 14, fontWeight: "700" },

  availabilityWrap: { marginTop: 4 },
  avRow: { flexDirection: "row", alignItems: "center", marginBottom: 8, gap: 8 },
  avDay: { width: 96, fontSize: 13, fontWeight: "900", color: "#111" },
  timeBtn: {
    flex: 1,
    backgroundColor: "#f5f5f5",
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 12,
    paddingVertical: 10,
    alignItems: "center",
  },
  timeBtnEmpty: { backgroundColor: "#fff", borderColor: "#e5e7eb" },
  timeBtnText: { color: "#111", fontWeight: "900", fontSize: 13 },

  termsRow: { flexDirection: "row", alignItems: "flex-start" },
  checkbox: { marginRight: 10, marginTop: 1 },
  termsText: { flex: 1, fontSize: 12, color: "#111", lineHeight: 16 },
  linkText: { color: "#2563eb", textDecorationLine: "underline", fontWeight: "900" },

  primaryBtn: {
    marginTop: 14,
    backgroundColor: "#FDF05D",
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  primaryBtnText: { color: "#000", fontWeight: "900", fontSize: 15 },

  loadingWrap: { flex: 1, justifyContent: "center", alignItems: "center", padding: 24 },
  loadingText: { marginTop: 10, color: "#555", fontSize: 14 },
  errorText: { color: "#111", fontSize: 14, fontWeight: "900", marginBottom: 12 },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    backgroundColor: "#fff",
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: "#eee",
  },
  modalTitle: { fontSize: 18, fontWeight: "900", color: "#111" },
  modalMeta: { marginTop: 6, marginBottom: 10, fontSize: 12, color: "#666" },
  timeList: { maxHeight: 360, borderRadius: 14, borderWidth: 1, borderColor: "#eee" },
  timeItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f1f1",
    alignItems: "center",
    backgroundColor: "#fff",
  },
  timeText: { fontSize: 15, color: "#111", fontWeight: "900" },
  modalCancel: {
    marginTop: 12,
    backgroundColor: "#f5f5f5",
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#eee",
  },
  modalCancelText: { color: "#111", fontWeight: "900" },
});

/* ---------------- SIDE NAV STYLES ---------------- */
const snav = StyleSheet.create({
  sideNav: { width: 250, backgroundColor: "#000" },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: "hidden",
    position: "relative",
  },
  sideNavGlow: {
    position: "absolute",
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: { flexDirection: "row", alignItems: "center", marginBottom: 26, zIndex: 2 },
  sideNavLogoImage: { width: 38, height: 38, marginRight: 10 },
  logoWordmark: { fontSize: 16, color: "#f5f5f5", fontWeight: "600", letterSpacing: 0.3 },
  logoDot: { color: "#FDF05D", fontSize: 18, fontWeight: "800" },
  logoDesktopTag: { fontSize: 9, color: "#b3b3b3", letterSpacing: 2, marginTop: 2 },
  sideNavSection: { marginTop: 12, marginBottom: 20, zIndex: 2 },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: "flex-start",
    maxWidth: "100%",
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: 1,
    textAlign: "left",
  },
  navItem: { flexDirection: "row", alignItems: "center", paddingVertical: 9, paddingHorizontal: 8, borderRadius: 10, marginBottom: 6 },
  navItemActive: { backgroundColor: "rgba(255,255,255,0.06)", borderWidth: 1, borderColor: "rgba(253,240,93,0.18)" },
  navIcon: { marginRight: 10 },
  navItemRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", flex: 1 },
  navItemMainLabel: { flexDirection: "row", alignItems: "center", flexShrink: 1 },
  navText: { color: "#f5f5f5", fontSize: 14, fontWeight: "500" },
  lockIcon: { marginLeft: 6 },

  peerMapCard: { borderRadius: 14, overflow: "hidden", marginTop: 4, marginBottom: 10 },
  peerMapInner: { paddingVertical: 10, paddingHorizontal: 10, borderRadius: 14, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)" },
  peerMapContent: { flexDirection: "row", alignItems: "center" },
  peerMapTitle: { color: "#f5f5f5", fontSize: 13, fontWeight: "500" },
  peerMapSubtitle: { color: "#b3b3b3", fontSize: 11, marginTop: 2 },

  subscribeCard: { borderRadius: 14, overflow: "hidden", marginTop: 10 },
  subscribeGradient: { paddingVertical: 10, paddingHorizontal: 12 },
  subscribeContent: { flexDirection: "row", alignItems: "center" },
  subscribeTitle: { fontSize: 13, fontWeight: "500", color: "#000" },
  subscribeSubtitle: { fontSize: 11, color: "#1f2933", marginTop: 2 },

  sideNavBottom: { marginTop: "auto", paddingTop: 12, borderTopWidth: 1, borderTopColor: "#181818", zIndex: 2 },
  bottomLink: { flexDirection: "row", alignItems: "center", paddingVertical: 6 },
  bottomLinkText: { fontSize: 13, color: "#f5f5f5", fontWeight: "600" },
  bottomLinkCaption: { fontSize: 11, color: "#b3b3b3", marginTop: 2 },
});
